import { Inject, Injectable } from '@angular/core';
import * as _ from 'underscore';
import { WindowPropertiesService } from './window-properties.service';
import { IExtendedWindow, IObject } from '../../../type_definitions/datatypes';
import { DOCUMENT } from '@angular/common';
import { BrowserVendorService } from '../browser/browser-vendor.service';

@Injectable()
export class BreakpointsService {
  private currentWidth: number = -1;
  private lastBPIndex: number = -1;
  private currentBPLabel: string;
  private readonly breakpoints: IObject[] = [
    {
      w: 1200,
      label: 'lg'
    },
    {
      w: 992,
      label: 'md'
    },
    {
      w: 768,
      label: 'sm'
    },
    {
      w: 480,
      label: 'xs'
    }
  ];
  private body: JQuery<HTMLElement> = $('body');

  constructor(
    private windowPropertiesService: WindowPropertiesService,
    private browserVendorService: BrowserVendorService,
    @Inject(Window) private window: IExtendedWindow,
    @Inject(DOCUMENT) private document: Document
  ) {
    /**/
  }

  /**
   * @ngdoc method
   * @name start
   * @methodOf BreakpointsService
   * @description
   * starts checking for breakpoints and updating them. Adds the current breakpoint class to the body.
   */
  public start(): void {
    this.check();

    if (!this.currentWidth && this.window.respond && this.window.respond.force) {
      //in IE8, we start out with a width of 0 when our iframe is hidden, and we don't get a resize event
      //we need to react as soon as we do have a width and force respond to react
      const task = setInterval(() => {
        this.check();
        if (this.currentWidth) {
          clearInterval(task);
          this.startListening();
          this.window.respond.force();
        }
      }, 100);
    } else {
      this.startListening();
    }
  }

  /**
   * @ngdoc method
   * @name stop
   * @methodOf BreakpointsService
   * @description
   * Stops checking for breakpoints and updating them. Removes all breakpoint classes from the body.
   */
  public stop(): void {
    //stop listening to resize events
    $(this.window).off('resize.breakpoints');

    this.currentBPLabel = undefined;

    //remove all css classes
    _.forEach(this.breakpoints, (bp: IObject) => {
      const cssClass = `bp-${bp.label}`;
      if (this.body.hasClass(cssClass)) {
        this.body.removeClass(`bp-${bp.label}`);
      }
    });
  }

  /**
   * @ngdoc method
   * @name check
   * @methodOf BreakpointsService
   * @description
   * adds and removes breakpoint classes from body if necessary
   */
  public check(): void {
    const width = this.getMediaWidth();
    if (width === this.currentWidth) {
      //nothing to do
      return;
    }

    const bpIndex: number = this.getBreakpointIndex(width);
    if (bpIndex === this.lastBPIndex) {
      //nothing to do
      return;
    }

    let i, bp, cssClass;
    if (this.lastBPIndex === -1) {
      // do nothing
    } else if (bpIndex > this.lastBPIndex) { //screen got smaller
      for (i = this.lastBPIndex; i < bpIndex; i++) {
        bp = this.breakpoints[ i ];
        cssClass = `bp-${bp.label}`;
        this.body.removeClass(cssClass);
        $(this.window).trigger(`exit-${cssClass}`);
        $(this.window).trigger('exit-bp', bp.label);
      }
    } else { //screen got bigger
      for (i = this.lastBPIndex; i > bpIndex; i--) {
        bp = this.breakpoints[ i ];
        cssClass = `bp-${bp.label}`;
        this.body.removeClass(cssClass);
        $(this.window).trigger(`exit-${cssClass}`);
        $(this.window).trigger('exit-bp', bp.label);
      }
    }

    bp = this.breakpoints[ bpIndex ];
    this.currentBPLabel = bp.label;
    cssClass = `bp-${this.currentBPLabel}`;
    this.body.addClass(cssClass);
    $(this.window).trigger(`enter-${cssClass}`);
    $(this.window).trigger('enter-bp', bp.label);

    this.currentWidth = width;
    this.lastBPIndex = bpIndex;
  }

  /**
   * @ngdoc method
   * @name current
   * @methodOf BreakpointsService
   * @description
   *  returns the current breakpoint (one of xs, sm, md, lg).
   *  Depending on the state of the service (started or not), this will calculate or rather return the last seen breakpoint
   * @returns {string} current breakpoint
   */
  public current(): string {
    return this.currentBPLabel || this.breakpoints[ this.getBreakpointIndex(this.getMediaWidth()) ].label;
  }

  /**
   * @ngdoc method
   * @name getBreakpoint
   * @methodOf BreakpointsService
   * @description
   * get breakpoint value by name
   */
  public getBreakpoint(key: string): number {
    return _.findWhere(this.breakpoints, { label: key }).w;
  }

  private startListening(): void {
    $(this.window).on('resize.breakpoints', _.debounce(() => {
      this.check();
    }, 100));
  }

  private getBreakpointIndex(w: number): number {
    for (let i = 0; i < this.breakpoints.length; i++) {
      if (w >= this.breakpoints[ i ].w) {
        return i;
      }
    }
    return this.breakpoints.length - 1;
  }

  private getMediaWidth(): number {
    if (this.browserVendorService.safari) {
      return this.document.body.clientWidth;
    }
    return this.windowPropertiesService.innerWidth();
  }
}